/*
  lucky_draw — turns the entry form into a multi-step one (the 7-section,
  32-question Phase 1 application; see the section file's top comment).

  Imported page-wise by sections/munchief-lucky-draw.liquid (defer); binds
  every instance on the page and re-inits on shopify:section:load.

  Progressive enhancement: the markup ships as ONE form with every fieldset
  visible, the progress bar and the Continue/Back buttons hidden, and a live
  submit button. Everything below is what turns that into the stepped comp,
  so a visitor whose script never loads still sees every field and can still
  post the entry.

  Three kinds of conditional markup, all driven by data attributes so the
  Liquid stays declarative:
    - data-lucky-show-if="id:value1|value2" on a fieldset or a .lucky-field —
      shown only once the named field (data-lucky-id="id") holds one of the
      given values. Used for the whole Video Review Track fieldset (keyed off
      the review-track answer) and for the allergy-detail textarea (keyed off
      the allergy-flag radio).
    - data-lucky-block-value="No" on a radio input, paired with a sibling
      [data-lucky-notice-for] — a disqualifying answer. The matching notice
      shows and Continue/Submit disable while it's the current selection.
    - data-lucky-notice-for + data-lucky-notice-value — a heads-up (not
      disqualifying), e.g. the "other city" notice under the delivery-cluster
      select.
  A [data-lucky-group][data-lucky-max] checkbox group additionally caps how
  many of its boxes can be checked at once (the "select up to four" question).

  The step is not remembered between page loads — a rejected post comes back
  from Shopify as a fresh render, and the visitor should land on step 1 with
  the errors in view.
*/
(function () {
  function initForm(form) {
    if (form.dataset.luckyReady === 'true') return;

    var steps = Array.prototype.slice.call(form.querySelectorAll('[data-lucky-step]'));
    var next = form.querySelector('[data-lucky-next]');
    var back = form.querySelector('[data-lucky-back]');
    var submit = form.querySelector('[data-lucky-submit]');
    // The success state renders neither steps nor buttons.
    if (!steps.length || !next || !back || !submit) return;

    form.dataset.luckyReady = 'true';
    form.classList.add('is-stepped');

    var progress = form.querySelector('[data-lucky-progress]');
    var progressLabel = form.querySelector('[data-lucky-progress-label]');
    var progressFill = form.querySelector('[data-lucky-progress-fill]');
    var currentStep = 1;

    function show(element, visible) {
      if (!element) return;
      if (visible) {
        element.removeAttribute('hidden');
      } else {
        element.setAttribute('hidden', '');
      }
    }

    // Every input/select/textarea sharing a data-lucky-id is one logical
    // question — one radio group, one checkbox group, or a single field.
    function fieldValue(id) {
      var controls = Array.prototype.slice.call(form.querySelectorAll('[data-lucky-id="' + id + '"]'));
      if (!controls.length) return '';
      if (controls[0].type === 'checkbox') {
        return controls.filter(function (c) { return c.checked; }).map(function (c) { return c.value; });
      }
      if (controls[0].type === 'radio') {
        var checked = controls.filter(function (c) { return c.checked; })[0];
        return checked ? checked.value : '';
      }
      return controls[0].value;
    }

    function showIfMatches(el) {
      var parts = el.dataset.luckyShowIf.split(':');
      var val = fieldValue(parts[0]);
      var values = parts[1].split('|');
      return Array.isArray(val) ? val.some(function (v) { return values.indexOf(v) !== -1; }) : values.indexOf(val) !== -1;
    }

    // A step fieldset with its own data-lucky-show-if (the Video Review
    // Track) is only ever a candidate for the current step when its
    // condition holds — goTo/nextVisibleStep decide the rest.
    function stepEnabled(fieldset) {
      return !fieldset.hasAttribute('data-lucky-show-if') || showIfMatches(fieldset);
    }

    /* Conditional sub-fields (not whole steps — those are handled by goTo),
       disqualifying notices and heads-up notices all recompute together —
       cheap enough to run on every change, and it keeps them from drifting
       out of sync with each other. */
    function updateConditionals() {
      form.querySelectorAll('[data-lucky-show-if]:not([data-lucky-step])').forEach(function (el) {
        show(el, showIfMatches(el));
      });

      form.querySelectorAll('[data-lucky-notice-for]').forEach(function (notice) {
        var id = notice.dataset.luckyNoticeFor;
        var values = notice.dataset.luckyNoticeValue.split('|');
        var val = fieldValue(id);
        show(notice, values.indexOf(val) !== -1);
      });

      syncRequired();
      updateBlockedState();

      // The review-track answer (step 5) can turn step 6 on or off while
      // the visitor is sitting on an earlier step; if it just got disabled
      // while the visitor is already past it, jump them straight to 7 so
      // Back/Continue never land on a fieldset that can no longer show.
      var current = steps.filter(function (fieldset) { return fieldset.dataset.luckyStep === String(currentStep); })[0];
      if (current && !stepEnabled(current)) goTo(nextVisibleStep(currentStep, 1));
    }

    /* A hidden control still takes part in constraint validation, and a
       required one the visitor can neither see nor focus makes the browser
       refuse the submit with nothing on screen to fix. So requiredness
       follows visibility — both "not the current step" and "hidden by its
       own data-lucky-show-if" count as invisible. The fields stay enabled
       either way — a disabled field is not posted, and earlier steps'
       answers must still travel with the final submit. */
    function syncRequired() {
      form.querySelectorAll('[data-lucky-required="true"]').forEach(function (field) {
        var step = field.closest('[data-lucky-step]');
        var inCurrentStep = step && step.dataset.luckyStep === String(currentStep);
        field.required = Boolean(inCurrentStep) && !field.closest('[hidden]');
      });
    }

    // Disqualifying ("block") answers disable Continue/Submit rather than
    // swap to a separate end screen — this form still has to post through
    // Shopify's own contact endpoint, so every visitor's answers travel
    // together in the one submission regardless of fit.
    function updateBlockedState() {
      var blocked = false;
      form.querySelectorAll('[data-lucky-block-value]').forEach(function (input) {
        if (input.checked && input.value === input.dataset.luckyBlockValue && !input.closest('[hidden]')) {
          blocked = true;
        }
      });
      next.disabled = blocked;
      submit.disabled = blocked;
    }

    // "Select up to N" checkbox groups — reverts a check past the cap
    // instead of disabling the remaining boxes, so the visitor can still
    // swap one answer for another without unchecking first.
    form.addEventListener('change', function (event) {
      var group = event.target.closest('[data-lucky-group][data-lucky-max]');
      if (!group || event.target.type !== 'checkbox' || !event.target.checked) return;
      var max = parseInt(group.dataset.luckyMax, 10);
      var checked = group.querySelectorAll('input:checked');
      if (max && checked.length > max) event.target.checked = false;
    });

    form.addEventListener('change', updateConditionals);
    form.addEventListener('input', updateConditionals);

    function visibleSteps() {
      return steps.filter(stepEnabled);
    }

    /* The step swap is a hard cut without this: one fieldset disappears and a
       taller/shorter one takes its place in the same frame. With GSAP the
       incoming step slides in from the direction of travel — forward from
       the right, Back from the left — so the two steps read as one form
       moving rather than two forms. Skipped whole where GSAP is missing or
       the visitor asked for less motion; the cut still works. */
    var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var stepped = false; // flipped after the initial goTo(1) below

    function animateIn(fieldset, direction) {
      // The first call is the initial render, not a step change — the card's
      // own reveal (munchief-lucky-motion.js) already brings that one in.
      if (!stepped || reducedMotion || !window.gsap) return;
      window.gsap.fromTo(
        fieldset,
        { opacity: 0, x: 26 * direction },
        { opacity: 1, x: 0, duration: 0.4, ease: 'power2.out', overwrite: 'auto' }
      );
    }

    function goTo(step) {
      var direction = step >= currentStep ? 1 : -1;
      currentStep = step;

      // Only the current step is ever shown — a disabled conditional step
      // (its own data-lucky-show-if false) never becomes current in the
      // first place, since nextVisibleStep skips it.
      steps.forEach(function (fieldset) {
        var isCurrent = fieldset.dataset.luckyStep === String(step);
        show(fieldset, isCurrent);
        if (isCurrent) animateIn(fieldset, direction);
      });

      var visible = visibleSteps();
      var position = visible.indexOf(form.querySelector('[data-lucky-step="' + step + '"]'));
      var total = visible.length;

      show(next, position < total - 1);
      show(back, position > 0);
      show(submit, position === total - 1);

      if (progressFill) progressFill.style.width = ((position + 1) / total) * 100 + '%';
      if (progressLabel) {
        progressLabel.textContent = progressLabel.textContent.replace(/\d+ of \d+/, (position + 1) + ' of ' + total);
      }

      syncRequired();
      updateBlockedState();
    }

    function stepNumbers() {
      return steps.map(function (fieldset) { return parseInt(fieldset.dataset.luckyStep, 10); });
    }

    function nextVisibleStep(from, direction) {
      var numbers = stepNumbers();
      var i = numbers.indexOf(from);
      while (true) {
        i += direction;
        if (i < 0 || i >= numbers.length) return from;
        if (stepEnabled(steps[i])) return numbers[i];
      }
    }

    function stepIsValid(step) {
      var fieldset = steps.filter(function (element) {
        return element.dataset.luckyStep === String(step);
      })[0];
      if (!fieldset) return true;

      var fields = Array.prototype.slice.call(fieldset.querySelectorAll('input, select, textarea'));
      for (var i = 0; i < fields.length; i++) {
        if (fields[i].closest('[hidden]')) continue;
        if (!fields[i].checkValidity()) {
          fields[i].reportValidity();
          return false;
        }
      }

      // Native `required` on a checkbox demands THAT box specifically, which
      // is right for "confirm every statement" but wrong for "pick at least
      // one" — those groups skip `required` and get their own check here.
      var groups = Array.prototype.slice.call(fieldset.querySelectorAll('[data-lucky-min]'));
      for (var j = 0; j < groups.length; j++) {
        var group = groups[j];
        if (group.closest('[hidden]')) continue;
        var min = parseInt(group.dataset.luckyMin, 10) || 1;
        var checked = group.querySelectorAll('input:checked').length;
        if (checked < min) {
          var first = group.querySelector('input');
          first.setCustomValidity('Choose at least one option.');
          first.reportValidity();
          first.setCustomValidity('');
          return false;
        }
      }

      return true;
    }

    next.addEventListener('click', function () {
      if (next.disabled) return;
      if (stepIsValid(currentStep)) goTo(nextVisibleStep(currentStep, 1));
    });

    back.addEventListener('click', function () {
      goTo(nextVisibleStep(currentStep, -1));
    });

    // Enter inside an earlier step is an implicit submit. Advance instead —
    // otherwise the entry posts before every question has been seen.
    form.addEventListener('submit', function (event) {
      var visible = visibleSteps();
      var isLast = visible[visible.length - 1] === form.querySelector('[data-lucky-step="' + currentStep + '"]');
      if (isLast) return;
      event.preventDefault();
      if (stepIsValid(currentStep)) goTo(nextVisibleStep(currentStep, 1));
    });

    show(progress, true);
    updateConditionals();
    goTo(1);
    stepped = true;
  }

  /* Drifting clouds ------------------------------------------------------
     The comp's clouds are static art; left that way they read as stickers
     pasted on the gradient. Each one gets its own yoyo tween — a wide
     travel plus a small tilt, quick enough to read as weather — so the
     band feels like sky. Nothing here changes layout: the tweens only touch
     transforms, so the clouds keep the percentage positions the stylesheet
     gives them.

     GSAP is the theme's own vendored copy, loaded from layout/theme.liquid
     before any section script (both are deferred, so they run in document
     order). If it is missing the clouds simply stay put — the same
     "no animation, never broken" contract munchief-scripts.liquid documents. */
  var DRIFTS = [
    { x: 78, y: 30, rotation: 3, duration: 5.5 },
    { x: -96, y: 40, rotation: -4, duration: 6.5 },
    { x: 68, y: -34, rotation: 3.5, duration: 5.8 },
    { x: -58, y: 26, rotation: -3, duration: 4.6 }
  ];

  function initClouds(section) {
    if (section.dataset.luckyCloudsReady === 'true') return;

    var clouds = Array.prototype.slice.call(section.querySelectorAll('.lucky-draw__cloud'));
    if (!clouds.length) return;

    // A visitor who asked for less motion gets the comp's static sky.
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reduced && reduced.matches) {
      section.dataset.luckyCloudsReady = 'true';
      return;
    }

    if (!window.gsap) return;
    section.dataset.luckyCloudsReady = 'true';

    clouds.forEach(function (cloud, index) {
      var drift = DRIFTS[index % DRIFTS.length];
      window.gsap.to(cloud, {
        x: drift.x,
        y: drift.y,
        rotation: drift.rotation,
        duration: drift.duration,
        delay: index * 0.35, // breaks the lockstep a shared start would give
        ease: 'sine.inOut',
        repeat: -1,
        yoyo: true
      });
    });
  }

  function initAll(root) {
    var scope = root || document;
    scope.querySelectorAll('.lucky-form').forEach(initForm);
    scope.querySelectorAll('.lucky-draw').forEach(initClouds);
  }

  initAll();

  // GSAP is deferred too. In document order it wins, but a section rendered
  // on its own (theme editor, ?sections= re-render) can run this first — so
  // the clouds get one more chance once everything has loaded.
  window.addEventListener('load', function () {
    initAll();
  });

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
